import React from 'react';

import { Autocomplete, OptionLabel } from './Autocomplete';
import { filterSuggestions, valueFromSuggestion, valueFromTyped } from './suggestAutocompleteHelpers';


/**
 * Free-text combobox over the presentation-only `Autocomplete` primitive where
 * THE VALUE IS THE TYPED STRING. Unlike the strict `OptionAutocomplete`, there is
 * no selected-option state: catalog matches appear as suggestions and clicking
 * one fills the text, but a typed value not in the suggestion list is kept as-is.
 *
 * Wiring: `query={value}` (the typed string drives the input); `onQueryChange`
 * emits the typed text immediately; `onChange(option)` fills the text with the
 * picked suggestion's label. `value={null}` is passed to the primitive so it
 * never renders a strict selected-check. Suggestions are filtered client-side by
 * case-insensitive substring on label via the pure helpers (unit-tested in the
 * node env). An empty value shows the full list and the placeholder.
 *
 * NOTE: `onFocus`/`onChange` are intentionally NOT forwarded to the primitive —
 * it spreads restProps AFTER its own input handlers, so passing them would
 * clobber its open-on-focus / typing behaviour.
 */
const SuggestAutocomplete = React.forwardRef((props, ref) => {
  const {
    value = '',
    onChange,
    options = [],
    placeholder,
    invalid = false,
    disabled = false,
    emptyText,
    id,
    className,
    loading = false,
  } = props;

  const filtered = filterSuggestions(options, value);

  return (
    <Autocomplete
      ref={ref}
      id={id}
      value={null}
      invalid={invalid}
      query={value ?? ''}
      disabled={disabled}
      className={className}
      emptyText={emptyText}
      loading={loading}
      options={filtered}
      placeholder={placeholder}
      renderOption={OptionLabel}
      getOptionKey={(o) => o.value}
      getOptionLabel={(o) => o.label}
      onQueryChange={(text) => onChange?.(valueFromTyped(text))}
      onChange={(option) => onChange?.(valueFromSuggestion(option))}
    />
  );
});

SuggestAutocomplete.displayName = 'SuggestAutocomplete';

export { SuggestAutocomplete };
export default SuggestAutocomplete;
